import { useState, useEffect } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createFieldMapping } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SYNC_MODE_LABELS } from "@shared/utils";
import { FieldMapping, SyncMode } from "../../../../../api/src/lib/models.js";

interface FieldMappingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mapping?: FieldMapping;
}

const FieldMappingForm = ({ isOpen, onClose, onSuccess, mapping }: FieldMappingFormProps) => {
  const [entityName, setEntityName] = useState(mapping?.entityName || "");
  const [kupField, setKupField] = useState(mapping?.kupField || "");
  const [hsField, setHsField] = useState(mapping?.hsField || "");
  const [isChoiceField, setIsChoiceField] = useState(mapping?.isChoiceField || false);
  const [syncMode, setSyncMode] = useState<SyncMode>(mapping?.syncMode || "ifEmpty");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (mapping) {
      setEntityName(mapping.entityName);
      setKupField(mapping.kupField);
      setHsField(mapping.hsField);
      setIsChoiceField(mapping.isChoiceField);
      setSyncMode(mapping.syncMode as SyncMode);
    }
  }, [mapping]);

  const mutation = useMutation({
    mutationFn: createFieldMapping,
    onSuccess: () => {
      toast({
        title: "Success",
        description: mapping ? "Field mapping updated successfully" : "Field mapping created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to ${mapping ? "update" : "create"} field mapping`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = {
      type: "field" as const,
      entityName,
      kupField,
      hsField,
      isChoiceField,
      syncMode,
    };

    mutation.mutate(formData);
  };

  // Include fields from the mapping in addition to the sample fields
  const baseEntities = ["pl_title", "pl_product"];
  const baseKupFields = ["pl_kup_main_type", "pl_kup_format", "pl_kup_title", "pl_kup_description", "pl_kup_release_date"];
  const baseHsFields = ["pl_hs_main_type", "pl_hs_format", "pl_hs_title", "pl_hs_description", "pl_hs_release_date"];
  
  // Ensure the fields from the mapping being edited are included in the options
  const entities = mapping?.entityName && !baseEntities.includes(mapping.entityName) 
    ? [...baseEntities, mapping.entityName] 
    : baseEntities;
    
  const kupFields = mapping?.kupField && !baseKupFields.includes(mapping.kupField) 
    ? [...baseKupFields, mapping.kupField] 
    : baseKupFields;
    
  const hsFields = mapping?.hsField && !baseHsFields.includes(mapping.hsField) 
    ? [...baseHsFields, mapping.hsField] 
    : baseHsFields;

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">{mapping ? "Edit" : "Add"} Field Mapping</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <div className="col-span-2">
              <Label htmlFor="entityName" className="block text-sm font-medium text-gray-700 mb-1">
                Entity Name
              </Label>
              <Select value={entityName} onValueChange={setEntityName} required>
                <SelectTrigger className="w-full px-3 py-2">
                  <SelectValue placeholder="Select an entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((entity) => (
                    <SelectItem key={entity} value={entity}>
                      {entity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="kupField" className="block text-sm font-medium text-gray-700 mb-1">
                KUP Field
              </Label>
              <Select value={kupField} onValueChange={setKupField} required>
                <SelectTrigger className="w-full px-3 py-2">
                  <SelectValue placeholder="Select KUP field" />
                </SelectTrigger>
                <SelectContent>
                  {kupFields.map((field) => (
                    <SelectItem key={field} value={field}>
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="hsField" className="block text-sm font-medium text-gray-700 mb-1">
                HS Field
              </Label>
              <Select value={hsField} onValueChange={setHsField} required>
                <SelectTrigger className="w-full px-3 py-2">
                  <SelectValue placeholder="Select HS field" />
                </SelectTrigger>
                <SelectContent>
                  {hsFields.map((field) => (
                    <SelectItem key={field} value={field}>
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">
                Is Choice Field
              </Label>
              <RadioGroup 
                value={isChoiceField ? "true" : "false"} 
                onValueChange={(value) => setIsChoiceField(value === "true")}
                className="flex items-center mt-2 space-x-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="choice-true" />
                  <Label htmlFor="choice-true">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="choice-false" />
                  <Label htmlFor="choice-false">No</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div>
              <Label htmlFor="syncMode" className="block text-sm font-medium text-gray-700 mb-1">
                Sync Mode
              </Label>
              <Select value={syncMode} onValueChange={(value) => setSyncMode(value as SyncMode)} required>
                <SelectTrigger className="w-full px-3 py-2">
                  <SelectValue placeholder="Select sync mode" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SYNC_MODE_LABELS) as SyncMode[]).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {SYNC_MODE_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2 bg-blue-50 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="material-icons text-blue-400">info</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Sync Mode Information</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li><strong>If Empty:</strong> Only sync KUP→HS if HS field is empty</li>
                      <li><strong>Always:</strong> Always overwrite HS with KUP value</li>
                      <li><strong>Ignore:</strong> Never sync this field pair</li>
                      <li><strong>Custom:</strong> Reserved for custom sync logic (not yet implemented)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0078D4] hover:bg-[#2B88D8]"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : mapping ? "Update Mapping" : "Create Mapping"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FieldMappingForm;
